import { DecimalPipe } from '@angular/common';
import { OverlayModule } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy, Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EnumRouteName, LANG_SIGNAL, Category, CategoryService, Genre, GenreService, LoggerService, LyricCardComponent, MusicStyle, MusicStyleService, popoverPositions, Recueil, RecueilService, ToastService, AutocompletePick, LsSearchAutocompletePanelComponent, LsSearchExtrasComponent, POPULAR_SEARCH_TAGS, SearchAutocompleteState, LyricSearchResultDto, SearchService } from '@lyricstream/shared';
import { Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, switchMap } from 'rxjs/operators';

export interface KeyDef { value: string; label: string; }
export interface ActiveChip { label: string; value: string; category: string; }

/** Résumé d'un recueil pour le filtre (local, pas de dépendance à @lyricstream/shared ici) */
//interface Recu.eil.Option { code: number; name: string; slug: string; }

/** Chants par page de résultats. */
const PAGE_SIZE = 10;
/** Le tiroir « Ajouter des chants » (mode embarqué) n'a pas de pagination : il garde une page plus longue. */
const EMBEDDED_PAGE_SIZE = 20;

@Component({
  selector: 'app-search',
  standalone: true,
  imports: [FormsModule, DecimalPipe, LyricCardComponent, OverlayModule, LsSearchAutocompletePanelComponent, LsSearchExtrasComponent, TranslateModule],
  templateUrl: './search.html',
  styleUrl: './search.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchComponent implements OnInit, OnDestroy {
  @ViewChild('queryInput') private queryInputRef?: ElementRef<HTMLInputElement>;

  private readonly searchService = inject(SearchService);
  private readonly toast = inject(ToastService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly recueilService = inject(RecueilService);
  private readonly categoryService = inject(CategoryService);
  private readonly genreService = inject(GenreService);
  private readonly styleService = inject(MusicStyleService);
  private readonly loggerService = inject(LoggerService);
  private readonly sanitizer = inject(DomSanitizer);
  protected readonly lang = inject(LANG_SIGNAL);
  private readonly translate = inject(TranslateService);

  /**
   * Mode embarqué (§8) — la page de recherche est réutilisée telle quelle dans le tiroir
   * "Ajouter des chants" de playlist-detail, sans header/footer/pagination/hero applicatifs.
   */
  readonly embedded = input<boolean>(false);

  /**
   * IDs de chants déjà présents dans la playlist cible (mode embarqué uniquement) — pilote
   * l'état `added` du bouton `+` de chaque carte. Fourni par le parent (playlist-detail), qui
   * reste l'unique source de vérité du contenu de la playlist — cf. design "Architectural
   * Considerations".
   */
  readonly existingLyricIds = input<Set<string>>(new Set());

  /**
   * ID de la playlist cible (mode embarqué uniquement) — transmis à chaque ls-lyric-card via
   * [playlistContext], qui fait l'appel API d'ajout en interne (cf. LsLyricCardComponent.
   * onAddClick). Sans cet id, playlistContext() est undefined et le bouton `+` ne fait rien
   * silencieusement (bug constaté : "pk ce bouton ne marche plus").
   */
  readonly playlistId = input<string>();
  protected readonly playlistContext = computed(() => {
    const id = this.playlistId();
    return id ? { playlistId: id } : undefined;
  });

  /**
   * Émis en mode embarqué après un ajout RÉUSSI (ls-lyric-card a déjà fait l'appel API — cf.
   * (trackAddedToPlaylist) ci-dessous) — informatif seulement, pour que le parent
   * (playlist-detail) mette à jour sa tracklist affichée localement. Ne PAS refaire d'appel API
   * ici : ce n'est plus sa responsabilité depuis que ls-lyric-card gère l'ajout en interne via
   * playlistContext (contrairement à l'ancien design où onAddTrack() ne faisait qu'émettre,
   * cf. commentaire historique conservé sur isLyricAdded ci-dessous).
   */
  readonly trackAdded = output<LyricSearchResultDto>();

  protected isLyricAdded(id: string): boolean {
    return this.existingLyricIds().has(id);
  }

  /**
   * Un chant sans audio ne doit pas proposer d'ajout à la playlist (cf. décision utilisateur
   * "si le lyric n'a aucun audio le bouton pour l'ajouter à la playlist ne doit pas être
   * visible") — audioUrl est déjà résolu côté middleware (mapLyricDto), donc pas besoin de
   * fouiller mediaFiles ici.
   */
  protected hasAudio(lyric: LyricSearchResultDto): boolean {
    // hasAudioFiles (pas audioUrl) : mapLyricDto (middleware) calcule hasAudioFiles/
    // defaultMedia/mediaAudios sur les résultats de recherche, jamais audioUrl — ce champ
    // n'existe que sur les Lyric déjà passés par MusicPlayerService.resolvePlayableSongs
    // (playlist/file d'attente). Avec `!!lyric.audioUrl` ici, hasAudio() était toujours false
    // en recherche : régression qui masquait le "+" pour tous les chants, pas seulement ceux
    // sans audio (cf. review).
    //
    // `!== false` (pas `!!`) : si l'enrichissement CoreService échoue côté middleware
    // (SearchService.searchLyrics, fallback "retour des hits bruts non enrichis"), les hits
    // Meilisearch bruts n'ont pas du tout ce champ (undefined, pas false) — dans ce cas précis
    // on affiche quand même le "+" (comportement d'avant ce lot, fail-open) plutôt que de
    // masquer l'ajout pour TOUTE la liste le temps que CoreService revienne (cf. review).
    return lyric.hasAudioFiles !== false;
  }

  /**
   * Appelé par (trackAddedToPlaylist) de ls-lyric-card — émis APRÈS un ajout serveur réussi
   * (l'appel API est fait en interne par la carte via [playlistContext], plus ici) : ce
   * handler ne fait donc plus que relayer au parent (playlist-detail) et nettoyer l'UI locale.
   * Anciennement branché sur un output `(add)` (avant clic, sans confirmation serveur) qui
   * n'existe plus sur ls-lyric-card — cf. bug constaté "pk ce bouton ne marche plus".
   */
  protected onAddTrack(lyric: LyricSearchResultDto): void {
    if (this.isLyricAdded(lyric.id)) return; // garde-fou — couvre clic répété, clavier, collage
    // Ferme l'autocomplete explicitement : le bouton `+` de ls-lyric-card appelle déjà
    // stopPropagation() sur son événement de clic (pour ne pas déclencher la navigation de la
    // carte), ce qui empêche aussi onDocumentClick() de le voir remonter jusqu'à document —
    // sans ce set explicite, l'autocomplete restait affichée par-dessus les résultats après un
    // ajout depuis une piste suggérée en autocomplete.
    this.ac.close();
    this.trackAdded.emit(lyric);
  }

  // ── Search ──────────────────────────────────────────────
  protected readonly query            = signal('');
  /** Autocomplétion partagée avec l'accueil et l'en-tête (suggestions, récentes, clavier). */
  protected readonly ac = new SearchAutocompleteState(this.searchService, inject(DestroyRef), POPULAR_SEARCH_TAGS);
  protected readonly popularTags = POPULAR_SEARCH_TAGS;

  // ── Filters ──────────────────────────────────────────────
  protected readonly selectedRecueils  = signal<number[]>([]);
  protected readonly selectedLanguages = signal<string[]>([]);
  protected readonly selectedGenres    = signal<string[]>([]);
  protected readonly selectedStyles    = signal<string[]>([]);
  protected readonly selectedCats      = signal<string[]>([]);
  protected readonly selectedKeys      = signal<string[]>([]);
  protected readonly keyMode           = signal<'any' | 'major' | 'minor'>('any');
  protected readonly bpmMin            = signal(60);
  protected readonly bpmMax            = signal(180);

  /**
   * Filtre rapide "a un média audio/partition" — cases indépendantes, accessibles directement
   * sur la page (PAS dans le panneau Filtres) pour éviter le fouillis constaté par l'utilisateur
   * (cf. image 1 de sa remarque) : "ajouter un filtre supplémentaire pour tier les lyrics qui
   * on des medias audio et partition. accessible depuis la page, pas dans le menu filtre".
   */
  protected readonly hasAudioFilter     = signal(false);
  protected readonly hasPartitionFilter = signal(false);

  // ── View / Sort / MS ─────────────────────────────────────
  // 'onecol' = une colonne, 'grid' = deux colonnes (défaut) — cf. design règle 13 : la v2
  // rend deux colonnes par défaut, la vue tableau et la notion "liste" tabulaire disparaissent.
  protected readonly view   = signal<'onecol' | 'grid'>('grid');
  protected readonly sortBy = signal('relevance');
  protected readonly msOpen = signal<string | null>(null);
  /** Requête de filtrage interne par dropdown multi-select (§10.1) — clé = nom du filtre (recueil/lang/genre/style/cat). */
  protected readonly msFilterQueries = signal<Record<string, string>>({});

  // ── Filtres — panneau flottant (§10.1) ────────────────────
  protected readonly filtersPanelOpen = signal(false);
  /**
   * Ancré via CDK Overlay (comme ls-share-menu/ls-card-lyric-menu/.sort-pop de playlist-detail)
   * — remplace l'ancien `.filters-panel { position: absolute; top: calc(100% + 8px); right: 0 }`
   * qui s'ancrait au premier parent `position: relative`, pas forcément le bouton "Filtres" lui-
   * même (même classe de bug que celle documentée dans ls-share-menu), cf. décision utilisateur
   * "le filtre doit être dans un MatMenu" → CDK Overlay pour rester cohérent avec le reste de
   * l'app (pas de @angular/material dans ce projet), cf. décision utilisateur "pas de MatMenu,
   * plutôt comme avant @angular/CDK".
   */
  private readonly filtersBtnRef = viewChild<ElementRef<HTMLButtonElement>>('filtersBtn');
  protected readonly filtersBtnEl = computed(() => this.filtersBtnRef()?.nativeElement ?? null);
  /** Positions partagées (cf. décision utilisateur, review : ce tableau était dupliqué à
   *  l'identique ici et dans playlist-detail.ts — centralisé dans Shared/common-tools.ts). */
  protected readonly popoverPositions = popoverPositions;

  // ── Pagination ────────────────────────────────────────────
  protected readonly page      = signal(1);
  protected readonly pageSize  = computed(() => (this.embedded() ? EMBEDDED_PAGE_SIZE : PAGE_SIZE));

  // ── État asynchrone ───────────────────────────────────────
  protected readonly isLoading = signal(false);
  protected readonly error     = signal<string | null>(null);
  protected readonly totalHits = signal(0);
  /** Terme pour lequel aucun chant ne contient tous les mots : la liste n'est alors qu'une proposition de chants proches. */
  protected readonly approximateFor = signal<string | null>(null);

  // ── Résultats (vrais hits du serveur) ─────────────────────
  protected readonly results   = signal<LyricSearchResultDto[]>([]);

  // ── Données de référence ──────────────────────────────────────────────────
  //readonly albums = signal<Album[]>([]);
  //protected readonly recueils  = signal<RecueilOption[]>([]);
  readonly recueils = signal<Recueil[]>([]);
  readonly allGenres = signal<Genre[]>([]);
  readonly allStyles = signal<MusicStyle[]>([]);
  readonly allCategories = signal<Category[]>([]);

  protected readonly KEYS: KeyDef[] = [
    { value: 'C',  label: 'C'  }, { value: 'C#', label: 'C♯' },
    { value: 'D',  label: 'D'  }, { value: 'D#', label: 'E♭' },
    { value: 'E',  label: 'E'  }, { value: 'F',  label: 'F'  },
    { value: 'F#', label: 'F♯' }, { value: 'G',  label: 'G'  },
    { value: 'G#', label: 'A♭' }, { value: 'A',  label: 'A'  },
    { value: 'A#', label: 'B♭' }, { value: 'B',  label: 'B'  },
  ];

  protected readonly LANGUAGES = [
    { code: 'ht', label: 'Krèyol'  },
    { code: 'fr', label: 'Français' },
    { code: 'en', label: 'English'  },
    { code: 'es', label: 'Español'  },
  ];

  // protected readonly GENRES = [
  //   'Adoration', 'Hymne', 'Gospel', 'Traditionnel', 'Contemporain', 'Compas gospel',
  // ];

  // protected readonly STYLES = [
  //   'Choral', 'Acoustique', 'Scénique', 'A cappella', 'Congrégational',
  // ];

  // protected readonly CATS = [
  //   'Adoration', 'Louange', 'Méditation', 'Pâques', 'Noël', 'Funérailles',
  //   'Mariage', 'École du dimanche', 'Baptême',
  // ];

  protected readonly BPM_PRESETS = [
    { key: 'SLOW',     label: 'Lent',   sub: '60–80',   min: 60,  max: 80  },
    { key: 'MODERATE', label: 'Modéré', sub: '80–100',  min: 80,  max: 100 },
    { key: 'LIVELY',   label: 'Vif',    sub: '100–130', min: 100, max: 130 },
    { key: 'FAST',     label: 'Rapide', sub: '130+',    min: 130, max: 200 },
  ];

  // ── Computed ──────────────────────────────────────────────

  protected readonly totalPages = computed(() =>
    Math.max(1, Math.ceil(this.totalHits() / this.pageSize()))
  );

  protected readonly pages = computed(() => {
    const total = this.totalPages();
    const current = this.page();
    const range: number[] = [];
    const delta = 2;
    for (let i = Math.max(1, current - delta); i <= Math.min(total, current + delta); i++) {
      range.push(i);
    }
    return range;
  });

  protected readonly activeChips = computed<ActiveChip[]>(() => {
    const chips: ActiveChip[] = [];
    for (const code of this.selectedRecueils()) {
      const name = this.recueils().find(r => r.code === code)?.name ?? String(code);
      chips.push({ label: name, value: String(code), category: 'recueil' });
    }
    for (const code of this.selectedLanguages()) {
      const name = this.LANGUAGES.find(l => l.code === code)?.label ?? code;
      chips.push({ label: name, value: code, category: 'langue' });
    }
    for (const id of this.selectedGenres()) {
      const name = this.allGenres().find(g => g.id === id)?.name ?? id;
      chips.push({ label: name, value: id, category: 'genre' });
    }
    for (const id of this.selectedStyles()) {
      const name = this.allStyles().find(s => s.id === id)?.name ?? id;
      chips.push({ label: name, value: id, category: 'style' });
    }
    for (const id of this.selectedCats()) {
      const name = this.allCategories().find(c => c.id === id)?.name ?? id;
      chips.push({ label: name, value: id, category: 'catégorie' });
    }
    return chips;
  });

  protected readonly activeFilterCount = computed(() =>
    this.selectedRecueils().length +
    this.selectedLanguages().length +
    this.selectedGenres().length +
    this.selectedStyles().length +
    this.selectedCats().length +
    this.selectedKeys().length +
    (this.bpmMin() !== 60 || this.bpmMax() !== 180 ? 1 : 0)
  );

  // ── Dropdowns filtrés (§10.1 — recherche interne par option) ──────────────

  protected readonly filteredRecueils = computed(() =>
    this.recueils().filter(r => this.matchesMsQuery('recueil', r.name)));

  protected readonly filteredLanguages = computed(() =>
    this.LANGUAGES.filter(l => this.matchesMsQuery('lang', l.label)));

  protected readonly filteredGenres = computed(() =>
    this.allGenres().filter(g => this.matchesMsQuery('genre', g.name)));

  protected readonly filteredStyles = computed(() =>
    this.allStyles().filter(s => this.matchesMsQuery('style', s.name)));

  protected readonly filteredCategories = computed(() =>
    this.allCategories().filter(c => this.matchesMsQuery('cat', c.name)));

  /**
   * Nombre de chants du recueil — seul filtre pour lequel un compteur est réellement exposé
   * par le modèle (`Recueil.lyricsCount`). Genre/Style/Catégorie n'ont pas d'équivalent
   * aujourd'hui : pas de compteur affiché pour ces trois dropdowns plutôt qu'une valeur inventée.
   */
  protected recueilLyricCount(code: number): number {
    return this.recueils().find(r => r.code === code)?.lyricsCount ?? 0;
  }

  protected readonly bpmRangeStyle = computed(() => {
    const min = 40, max = 200;
    const left  = ((this.bpmMin() - min) / (max - min)) * 100;
    const right = ((this.bpmMax() - min) / (max - min)) * 100;
    return `left:${left}%;right:${100 - right}%`;
  });

  // ── Streams RxJS pour le debounce ─────────────────────────

  /** Subject pour la recherche principale (debounce 300ms) */
  private readonly searchTrigger$ = new Subject<void>();

  private readonly subscriptions = new Subscription();

  // ── Lifecycle ─────────────────────────────────────────────

  constructor() {
    // Effect : réagit aux changements de tous les filtres et de la page
    // pour déclencher la recherche via le Subject (avec debounce côté RxJS)
    effect(() => {
      // On lit tous les signals pour créer la dépendance
      void this.query();
      void this.selectedRecueils();
      void this.selectedLanguages();
      void this.selectedGenres();
      void this.selectedStyles();
      void this.selectedCats();
      void this.selectedKeys();
      void this.bpmMin();
      void this.bpmMax();
      void this.hasAudioFilter();
      void this.hasPartitionFilter();
      void this.sortBy();
      void this.page();
      this.searchTrigger$.next();
    });
  }

  async ngOnInit(): Promise<void> {
    // Recueils connus (liste statique temporaire — à remplacer par un appel API /api/recueils quand disponible)
    // this.recueils.set([
    //   { code: 1, name: 'Mélodies Joyeuses et Fraîches', slug: 'mjf' },
    //   { code: 2, name: "J'aime l'Éternel — Miracles",   slug: 'jem' },
    //   { code: 3, name: "Chants d'Espérance — Français", slug: 'cef' },
    //   { code: 4, name: "Chants d'Espérance — Krèyol",   slug: 'ce'  },
    //   { code: 5, name: 'Recueil Évangile de Grâce',     slug: 'reg' },
    // ]);

    await Promise.all([
      this.loadRecueils(),
      this.loadGenres(),
      this.loadStyles(),
      this.loadCategories(),
    ]);

    this.readFromUrl();
    this.setupSearchStream();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  // ── Load ─────────────────────────────────────

  private async loadRecueils(): Promise<void> {
    //if (!this.isAdmin()) return;
    try {
      const recueils = await this.recueilService.getRecueilsActive();
      this.recueils.set(recueils);
    } catch(e) {
      this.loggerService.debug('loadRecueils', e);
    }
  }

  private async loadGenres(): Promise<void> {
    try {
      const genres = await this.genreService.getTree();
      this.allGenres.set(genres);
    } catch(e) {
      this.loggerService.debug('loadGenres', e);
    }
  }

  private async loadStyles(): Promise<void> {
    try {
      // Charger tous les styles (pagination large)
      const result = await this.styleService.getByGenre('', 1, 200);
      this.allStyles.set(result.items ?? []);
    } catch(e) {
      this.loggerService.debug('loadStyles', e);
    }
  }

    private async loadCategories(): Promise<void> {
      try {
        const cats = await this.categoryService.getCategories();
        this.allCategories.set(cats);
      } catch(e) {
        this.loggerService.debug('loadCategories', e);
      }
    }

  // ── Setup des streams ─────────────────────────────────────

  private setupSearchStream(): void {
    const sub = this.searchTrigger$.pipe(
      debounceTime(300),
      switchMap(() => {
        this.isLoading.set(true);
        this.error.set(null);

        const filters = {
          query:      this.query(),
          recueils:   this.selectedRecueils(),
          languages:  this.selectedLanguages(),
          genres:     this.selectedGenres().map(id => this.allGenres().find(g => g.id === id)?.name ?? id),
          styles:     this.selectedStyles().map(id => this.allStyles().find(s => s.id === id)?.name ?? id),
          categories: this.selectedCats().map(id => this.allCategories().find(c => c.id === id)?.name ?? id),
          keys:       this.selectedKeys(),
          bpmMin:     this.bpmMin(),
          bpmMax:     this.bpmMax(),
          hasAudio:     this.hasAudioFilter(),
          hasPartition: this.hasPartitionFilter(),
          sortBy:     this.sortBy(),
          page:       this.page(),
          limit:      this.pageSize(),
        };

        return this.searchService.searchLyricsFiltered(filters).pipe(
          catchError(err => {
            console.error('[SearchComponent] Erreur lors de la recherche:', err);
            this.error.set(this.translate.instant('SEARCH_PAGE.ERROR'));
            return of(null);
          }),
        );
      }),
    ).subscribe(response => {
      this.isLoading.set(false);
      if (response) {
        this.results.set(response.hits);
        this.totalHits.set(response.totalHits);
        this.approximateFor.set(response.isApproximate === true ? response.query : null);
      }
    });

    this.subscriptions.add(sub);
  }

  // ── URL sync ──────────────────────────────────────────────

  private readFromUrl(): void {
    const p = this.route.snapshot.queryParamMap;
    const get  = (k: string) => p.get(k);
    const list = (k: string) => p.getAll(k).flatMap(v => v.split(',').filter(Boolean));

    if (get('q'))       this.query.set(get('q')!);
    if (get('sort'))    this.sortBy.set(get('sort')!);
    if (get('view'))    this.view.set(get('view') as 'onecol' | 'grid');
    if (get('keyMode')) this.keyMode.set(get('keyMode') as 'any' | 'major' | 'minor');
    if (get('bpmMin'))  this.bpmMin.set(+get('bpmMin')!);
    if (get('bpmMax'))  this.bpmMax.set(+get('bpmMax')!);
    if (get('hasAudio') === '1')     this.hasAudioFilter.set(true);
    if (get('hasPartition') === '1') this.hasPartitionFilter.set(true);
    if (get('page'))    this.page.set(Math.max(1, +get('page')!));

    const recueils = list('recueils').map(Number).filter(n => !isNaN(n));
    if (recueils.length) this.selectedRecueils.set(recueils);

    const langs = list('langs');
    if (langs.length) this.selectedLanguages.set(langs);

    const genres = list('genres');
    if (genres.length) this.selectedGenres.set(genres);

    const styles = list('styles');
    if (styles.length) this.selectedStyles.set(styles);

    const cats = list('cats');
    if (cats.length) this.selectedCats.set(cats);

    const keys = list('keys');
    if (keys.length) this.selectedKeys.set(keys);
  }

  private syncUrl(): void {
    const params: Record<string, string | string[]> = {};

    if (this.query())    params['q']       = this.query();
    if (this.sortBy() !== 'relevance') params['sort'] = this.sortBy();
    if (this.view()   !== 'grid')      params['view'] = this.view();
    if (this.keyMode() !== 'any')      params['keyMode'] = this.keyMode();
    if (this.bpmMin() !== 60)          params['bpmMin'] = String(this.bpmMin());
    if (this.bpmMax() !== 180)         params['bpmMax'] = String(this.bpmMax());
    if (this.hasAudioFilter())         params['hasAudio']     = '1';
    if (this.hasPartitionFilter())     params['hasPartition'] = '1';
    if (this.page() > 1)               params['page']   = String(this.page());

    if (this.selectedRecueils().length)  params['recueils'] = this.selectedRecueils().join(',');
    if (this.selectedLanguages().length) params['langs']    = this.selectedLanguages().join(',');
    if (this.selectedGenres().length)    params['genres']   = this.selectedGenres().join(',');
    if (this.selectedStyles().length)    params['styles']   = this.selectedStyles().join(',');
    if (this.selectedCats().length)      params['cats']     = this.selectedCats().join(',');
    if (this.selectedKeys().length)      params['keys']     = this.selectedKeys().join(',');

    this.router.navigate([], { queryParams: params, replaceUrl: true });
  }

  // ── Search handlers ───────────────────────────────────────

  protected onQueryInput(value: string): void {
    this.query.set(value);
    this.page.set(1);
    this.syncUrl();
    // Déclencher l'autocomplete (le stream de recherche est déclenché par l'effect)
    this.ac.input(value);
  }

  protected clearQuery(): void {
    this.query.set('');
    this.page.set(1);
    this.ac.reset();
    this.syncUrl();
  }

  protected onAutocompleteSelect(item: AutocompletePick): void {
    // Une playlist n'est pas un critère de recherche de chants : on ouvre directement sa page publique.
    if (item.type === 'playlist' && item.id) {
      this.ac.close();
      this.ac.pushRecent(item.title);
      this.router.navigate(['/', this.lang(), EnumRouteName.Playlists, item.id]);
      return;
    }
    this.query.set(item.title);
    this.page.set(1);
    this.ac.close();
    this.ac.pushRecent(item.title);
    this.syncUrl();
  }

  /** Focus le champ de recherche + ouvre l'autocomplete (§10.4, ⌘K/Ctrl+K). */
  protected onSearchInputFocus(): void {
    this.ac.focus(this.query());
  }

  protected onQueryKeydown(event: KeyboardEvent): void {
    this.ac.handleKeydown(event, () => this.onSearchSubmit(), (item) => this.onAutocompleteSelect(item));
  }

  // ── Pagination ────────────────────────────────────────────

  protected goToPage(p: number): void {
    if (p < 1 || p > this.totalPages()) return;
    this.page.set(p);
    this.syncUrl();
  }

  // ── Filter handlers ───────────────────────────────────────

  protected clearFilters(): void {
    this.selectedRecueils.set([]);
    this.selectedLanguages.set([]);
    this.selectedGenres.set([]);
    this.selectedStyles.set([]);
    this.selectedCats.set([]);
    this.selectedKeys.set([]);
    this.keyMode.set('any');
    this.bpmMin.set(60);
    this.bpmMax.set(180);
    this.msOpen.set(null);
    this.page.set(1);
    this.syncUrl();
  }

  // ── Filtre rapide média (hors panneau Filtres) ────────────
  protected toggleHasAudioFilter(): void {
    this.hasAudioFilter.update((v) => !v);
    this.page.set(1);
    this.syncUrl();
  }

  protected toggleHasPartitionFilter(): void {
    this.hasPartitionFilter.update((v) => !v);
    this.page.set(1);
    this.syncUrl();
  }

  protected removeChip(chip: ActiveChip): void {
    if (chip.category === 'recueil')   this.toggleRecueil(+chip.value);
    if (chip.category === 'langue')    this.toggleLanguage(chip.value);
    if (chip.category === 'genre')     this.toggleGenre(chip.value);
    if (chip.category === 'style')     this.toggleStyle(chip.value);
    if (chip.category === 'catégorie') this.toggleCat(chip.value);
  }

  // ── Sort / View / KeyMode ─────────────────────────────────

  protected onSortChange(value: string): void   { this.sortBy.set(value); this.page.set(1); this.syncUrl(); }
  protected onViewChange(value: 'onecol' | 'grid'): void { this.view.set(value); this.syncUrl(); }
  protected onKeyModeChange(value: 'any' | 'major' | 'minor'): void { this.keyMode.set(value); this.syncUrl(); }

  // ── MS dropdown ───────────────────────────────────────────

  protected toggleMs(name: string, event: Event): void {
    event.stopPropagation();
    this.msOpen.set(this.msOpen() === name ? null : name);
  }

  /** Requête de filtrage interne du dropdown `name` (§10.1, .ms-pop-search) — vide par défaut. */
  protected msFilterQuery(name: string): string {
    return this.msFilterQueries()[name] ?? '';
  }

  protected setMsFilterQuery(name: string, value: string): void {
    this.msFilterQueries.update((cur) => ({ ...cur, [name]: value }));
  }

  private matchesMsQuery(name: string, label: string): boolean {
    const q = this.msFilterQuery(name).trim().toLowerCase();
    return !q || label.toLowerCase().includes(q);
  }

  // "Effacer" local par dropdown (§10.1, .ms-pop-foot) — distinct du "Tout effacer" global.
  protected clearRecueilFilter(): void  { this.selectedRecueils.set([]);  this.page.set(1); this.syncUrl(); }
  protected clearLanguageFilter(): void { this.selectedLanguages.set([]); this.page.set(1); this.syncUrl(); }
  protected clearGenreFilter(): void    { this.selectedGenres.set([]);    this.page.set(1); this.syncUrl(); }
  protected clearStyleFilter(): void    { this.selectedStyles.set([]);    this.page.set(1); this.syncUrl(); }
  protected clearCatFilter(): void      { this.selectedCats.set([]);      this.page.set(1); this.syncUrl(); }

  // ── Filtres — panneau flottant (§10.1) ────────────────────

  protected toggleFiltersPanel(event: Event): void {
    event.stopPropagation();
    this.filtersPanelOpen.update((v) => !v);
  }

  protected closeFiltersPanel(): void {
    this.filtersPanelOpen.set(false);
    this.msOpen.set(null);
  }

  /**
   * Échap ferme d'abord une liste déroulante ouverte, pas le panneau entier — cf. design règle 12.
   */
  protected onFiltersEscape(event: Event): void {
    event.stopPropagation();
    if (this.msOpen()) {
      this.msOpen.set(null);
      return;
    }
    this.closeFiltersPanel();
  }

  protected onSearchSubmit(): void {
    this.page.set(1);
    // Ferme l'autocomplete à la validation — le bouton "Chercher" est à l'intérieur de
    // .search-shell, donc onDocumentClick() ne le ferme jamais (target.closest('.search-shell')
    // reste vrai) : sans ce set explicite, le panneau d'autocomplete restait affiché par-dessus
    // les résultats après un clic sur "Chercher".
    this.ac.close();
    this.syncUrl();
    this.searchTrigger$.next();
  }

  // ── Toggle helpers ────────────────────────────────────────

  protected toggleRecueil(code: number): void {
    const cur = this.selectedRecueils();
    this.selectedRecueils.set(cur.includes(code) ? cur.filter(c => c !== code) : [...cur, code]);
    this.page.set(1);
    this.syncUrl();
  }

  protected toggleLanguage(code: string): void {
    const cur = this.selectedLanguages();
    this.selectedLanguages.set(cur.includes(code) ? cur.filter(c => c !== code) : [...cur, code]);
    this.page.set(1);
    this.syncUrl();
  }

  protected toggleGenre(g: string): void {
    const cur = this.selectedGenres();
    this.selectedGenres.set(cur.includes(g) ? cur.filter(x => x !== g) : [...cur, g]);
    this.page.set(1);
    this.syncUrl();
  }

  protected toggleStyle(s: string): void {
    const cur = this.selectedStyles();
    this.selectedStyles.set(cur.includes(s) ? cur.filter(x => x !== s) : [...cur, s]);
    this.page.set(1);
    this.syncUrl();
  }

  protected toggleCat(c: string): void {
    const cur = this.selectedCats();
    this.selectedCats.set(cur.includes(c) ? cur.filter(x => x !== c) : [...cur, c]);
    this.page.set(1);
    this.syncUrl();
  }

  protected toggleKey(key: string): void {
    const cur = this.selectedKeys();
    this.selectedKeys.set(cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key]);
    this.page.set(1);
    this.syncUrl();
  }

  // ── Predicate helpers ─────────────────────────────────────

  protected isRecueilSelected(code: number):  boolean { return this.selectedRecueils().includes(code); }
  protected isLanguageSelected(code: string): boolean { return this.selectedLanguages().includes(code); }
  protected isGenreSelected(g: string):       boolean { return this.selectedGenres().includes(g); }
  protected isStyleSelected(s: string):       boolean { return this.selectedStyles().includes(s); }
  protected isCatSelected(c: string):         boolean { return this.selectedCats().includes(c); }
  protected isKeySelected(key: string):       boolean { return this.selectedKeys().includes(key); }

  // ── BPM ───────────────────────────────────────────────────

  // Écart minimal entre les deux poignées = 4 (alignement sur la référence Recherche avancée v2.html) — design règle 12.
  protected onBpmMinChange(value: number): void { this.bpmMin.set(Math.min(value, this.bpmMax() - 4)); this.page.set(1); this.syncUrl(); }
  protected onBpmMaxChange(value: number): void { this.bpmMax.set(Math.max(value, this.bpmMin() + 4)); this.page.set(1); this.syncUrl(); }
  protected applyBpmPreset(p: { min: number; max: number }): void { this.bpmMin.set(p.min); this.bpmMax.set(p.max); this.page.set(1); this.syncUrl(); }
  protected isBpmPresetActive(p: { min: number; max: number }): boolean { return this.bpmMin() === p.min && this.bpmMax() === p.max; }

  // ── Helpers affichage ─────────────────────────────────────

  protected msLabel(selected: string[], placeholder: string): string {
    if (!selected.length) return placeholder;
    return selected.length === 1 ? selected[0] : `${selected[0]} +${selected.length - 1}`;
  }

  protected recueilTriggerLabel(): string {
    const sel = this.selectedRecueils();
    if (!sel.length) return '';
    return this.recueils().find(r => r.code === sel[0])?.name ?? String(sel[0]);
  }

  protected langTriggerLabel(): string {
    const sel = this.selectedLanguages();
    if (!sel.length) return '';
    return this.LANGUAGES.find(l => l.code === sel[0])?.label ?? sel[0];
  }

  /** Paire de couleurs de dégradé pour les covers absentes — props coverFrom/coverTo de LyricCard. */
  protected getArtColors(id: string | number): { c1: string; c2: string } {
    const pairs: [string, string][] = [
      ['#142C6B', '#FF6A1F'],
      ['#0E1B40', '#7BD5A4'],
      ['#6B3FA0', '#FFB68A'],
      ['#1F8A5B', '#FFE08A'],
      ['#B53D11', '#1456BE'],
      ['#7E2A0C', '#FF924F'],
    ];
    const index = typeof id === 'number' ? id : parseInt(id, 10) || 0;
    const [c1, c2] = pairs[((index % pairs.length) + pairs.length) % pairs.length];
    return { c1, c2 };
  }

  /**
   * Surligne le terme recherché dans un texte de résultat (titre) — présent dans la maquette
   * de référence sur les résultats eux-mêmes, pas seulement l'autocomplete (§10.2). Le texte
   * source est le titre du chant (donnée du catalogue, pas une saisie libre d'un tiers non fiable),
   * échappé avant insertion des balises <em> — sanitize() en filet de sécurité supplémentaire.
   */
  protected highlight(text: string): SafeHtml {
    const q = this.query().trim();
    const escapedText = this.escapeHtml(text);
    if (!q) return this.sanitizer.bypassSecurityTrustHtml(escapedText);
    const escapedQueryPattern = this.escapeHtml(q).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const highlighted = escapedText.replace(new RegExp(`(${escapedQueryPattern})`, 'ig'), '<em>$1</em>');
    return this.sanitizer.bypassSecurityTrustHtml(highlighted);
  }

  private escapeHtml(text: string): string {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  protected durationLabel(seconds?: number): string | undefined {
    if (!seconds) return undefined;
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
  }

  /** ⌘K / Ctrl+K — focus le champ de recherche depuis n'importe où sur la page (§10.4). */
  @HostListener('document:keydown', ['$event'])
  onGlobalKeydown(event: KeyboardEvent): void {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.queryInputRef?.nativeElement.focus();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    // .filters-panel vit désormais hors de .search-shell (rendu dans le cdk-overlay-container
    // via CDK Overlay, cf. filtersBtnRef) — sans cette exception, tout clic à l'intérieur du
    // panneau (case recueil, preset BPM…) était vu comme "hors .search-shell" et fermait
    // l'autocomplete par effet de bord (cf. review).
    if (!target.closest('.search-shell') && !target.closest('.filters-panel')) {
      this.ac.close();
    }
    if (!target.closest('.ms'))           this.msOpen.set(null);
    // .filters-panel lui-même fermé par le backdrop du CDK Overlay (closeFiltersPanel() sur
    // backdropClick/detach), pas par ce handler maison.
  }

  // ── Menu ⋮ des cartes de résultat (§10.3) — 5 groupes, en-tête = titre du chant ────────────
  // Ouverture/ancrage désormais gérés en interne par ls-lyric-card (cf. décision utilisateur
  // "le card aura tjrs un menu ... je ne veux pas avoir à l'appeler séparément") — la page ne
  // fait plus que réagir à l'action choisie.

  // ── Toast global (ToastService, monté une fois à la racine app.html) — cf. décision
  //    utilisateur "pk le toast n'est pas réutilisable genre un composant" / "pk ce n'est pas
  //    un service". Wrapper conservé pour ne pas réécrire tous les appels showToast(...)
  //    existants de cette page. ─────────────────────────────────────────────────────────

  protected showToast(html: string): void {
    this.toast.showHtml(html);
  }
}
